import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  faChevronDown,
  faFiles,
  faUpload,
} from "@fortawesome/pro-regular-svg-icons";
import { Root } from "@radix-ui/react-accordion";
import Dropzone, { DropzoneRef, FileRejection } from "react-dropzone";

import { accordionRootClassName } from "src/base-components/EditorAccordionItem";
import { Icon } from "src/base-components/Icon";
import { OptionsDropdown } from "src/base-components/OptionsDropdown/OptionsDropdown";
import { type OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import {
  FileCategory,
  type FileCategoryData,
} from "src/cases/caseView/FileCategory";
import {
  FILE_OPERATIONS,
  useFileCategoryOperation,
} from "src/cases/queries/cases";
import { CaseDeserialized } from "src/cases/queries/casesTypes";
import {
  GetFileEnvFilesFileIdGetEnvEnum,
  UploadFileEnvFilesPostEnvEnum,
} from "src/clients/files";
import { EmptyState } from "src/design-system/EmptyState";
import { toastActions } from "src/design-system/Toast/utils";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useCurrentUserId } from "src/store/AuthStore";
import {
  FileSource,
  humanReadableSize,
  MAX_FILE_SIZE_BYTES,
} from "src/utils/files";

type CaseFilesProps = {
  caseData?: CaseDeserialized;
  isLoading: boolean;
};

const ACCEPTED_FORMATS = ["txt", "pdf", "images", "csv", "json", "xlsx"];

const ACCEPTED_FILE_TYPES: Record<string, string[]> = {
  "application/pdf": [".pdf"],
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "text/plain": [".txt"],
  "text/csv": [".csv"],
  "application/json": [".json"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
};

const getFileUploadErrorMessage = (
  errorCode: string,
  fileName: string,
): string => {
  const errorMessages: Record<string, string> = {
    "file-too-large": `File size exceeds ${humanReadableSize(MAX_FILE_SIZE_BYTES)} limit`,
    "file-invalid-type": `Invalid file type. Supported types: ${ACCEPTED_FORMATS.join(", ")}`,
    "too-many-files": "Only 1 file allowed",
  };
  return (
    errorMessages[errorCode] ||
    `Upload failed for ${fileName}. Please try again.`
  );
};

export const CaseFiles: React.FC<CaseFilesProps> = ({
  caseData,
  isLoading,
}) => {
  const fileCategoryOperation = useFileCategoryOperation();
  const currentUserId = useCurrentUserId();
  const { caseFiles, cases } = useCapabilities();
  const dropzoneRef = useRef<DropzoneRef>(null);

  const [isUploading, setIsUploading] = useState(false);
  const [pendingCategory, setPendingCategory] = useState<string | null>(null);
  const [newlyUploadedFileIds, setNewlyUploadedFileIds] = useState<string[]>(
    [],
  );
  const [openAccordionItems, setOpenAccordionItems] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  const canEditFiles = useMemo(
    () =>
      !!(
        caseData?.data &&
        currentUserId &&
        (cases.canManage ||
          (caseFiles.canEdit && caseData.data.assignee?.id === currentUserId))
      ),
    [caseData?.data, currentUserId, cases.canManage, caseFiles.canEdit],
  );

  const canDeleteFiles = cases.canManage;

  const fileCategories = useMemo((): FileCategoryData[] => {
    if (!caseData?.data.file_categories) return [];

    return caseData.data.file_categories.map((category) => ({
      id: category.id,
      display_name: category.display_name,
      description: category.description || "",
      category_key: category.category_key,
      file_ids: category.file_ids || [],
    }));
  }, [caseData?.data.file_categories]);

  const currentFileIds = useMemo(
    () =>
      fileCategories.reduce<Record<string, string[]>>(
        (acc, cat) => ({ ...acc, [cat.category_key]: cat.file_ids }),
        {},
      ),
    [fileCategories],
  );

  const handleCategorySelect = useCallback((categoryKey: string) => {
    setPendingCategory(categoryKey);
    dropzoneRef.current?.open();
  }, []);

  const dropdownElements = useMemo(
    (): OptionsDropdownElement[] => [
      {
        key: "header",
        header: "Add new file to:",
      },
      ...fileCategories.map((cat) => ({
        key: cat.display_name,
        action: () => handleCategorySelect(cat.category_key),
      })),
    ],
    [fileCategories, handleCategorySelect],
  );

  const env =
    caseData?.data.env === "sandbox"
      ? GetFileEnvFilesFileIdGetEnvEnum.SANDBOX
      : GetFileEnvFilesFileIdGetEnvEnum.LIVE;

  const handleFileUploadWithCategory = useCallback(
    async (file: File, categoryKey: string) => {
      if (!caseData?.data.id) return;

      setIsUploading(true);

      const uploadEnv =
        env === GetFileEnvFilesFileIdGetEnvEnum.SANDBOX
          ? UploadFileEnvFilesPostEnvEnum.SANDBOX
          : UploadFileEnvFilesPostEnvEnum.LIVE;

      fileCategoryOperation.mutate(
        {
          type: FILE_OPERATIONS.UPLOAD,
          caseId: caseData.data.id,
          file,
          categoryKey,
          env: uploadEnv,
          source: "case-files" as FileSource,
          currentFileIds,
        },
        {
          onSuccess: (data) => {
            if ("fileId" in data && data.fileId) {
              setNewlyUploadedFileIds((prev) => [...prev, data.fileId]);
            }
          },
          onSettled: () => {
            setIsUploading(false);
            setPendingCategory(null);
          },
        },
      );
    },
    [caseData?.data.id, env, currentFileIds, fileCategoryOperation],
  );

  const handleFileSelect = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (acceptedFiles.length > 0 && pendingCategory) {
        handleFileUploadWithCategory(acceptedFiles[0], pendingCategory);
        return;
      }

      if (rejectedFiles.length > 0) {
        const firstRejection = rejectedFiles[0];
        const errorCode = firstRejection.errors[0]?.code;

        toastActions.failure({
          title: getFileUploadErrorMessage(errorCode, firstRejection.file.name),
        });
      }
    },
    [pendingCategory, handleFileUploadWithCategory],
  );

  const removeUploadedFileId = useCallback((fileId: string) => {
    setNewlyUploadedFileIds((prev) => prev.filter((id) => id !== fileId));
  }, []);

  const handleDeleteFile = useCallback(
    (fileId: string, fileName: string, categoryKey: string) => {
      if (!caseData?.data.id) return;

      fileCategoryOperation.mutate(
        {
          type: FILE_OPERATIONS.DELETE,
          caseId: caseData.data.id,
          fileId,
          fileName,
          categoryKey,
          currentFileIds,
        },
        {
          onSuccess: () => removeUploadedFileId(fileId),
        },
      );
    },
    [
      caseData?.data.id,
      currentFileIds,
      fileCategoryOperation,
      removeUploadedFileId,
    ],
  );

  const handleMoveFile = useCallback(
    (
      fileId: string,
      fileName: string,
      fromCategory: string,
      toCategory: string,
    ) => {
      if (!caseData?.data.id) return;

      fileCategoryOperation.mutate({
        type: FILE_OPERATIONS.MOVE,
        caseId: caseData.data.id,
        fileId,
        fileName,
        fromCategoryKey: fromCategory,
        toCategoryKey: toCategory,
        currentFileIds,
      });
    },
    [caseData?.data.id, currentFileIds, fileCategoryOperation],
  );

  const categoriesWithFiles = useMemo(
    () =>
      fileCategories
        .filter((cat) => cat.file_ids.length > 0)
        .map((cat) => cat.category_key),
    [fileCategories],
  );

  useEffect(() => {
    if (!initialized && fileCategories.length > 0) {
      setOpenAccordionItems(categoriesWithFiles);
      setInitialized(true);
    }
  }, [categoriesWithFiles, initialized, fileCategories.length]);

  useEffect(() => {
    if (initialized) {
      setOpenAccordionItems((prevOpen) =>
        prevOpen.filter((categoryKey) =>
          categoriesWithFiles.includes(categoryKey),
        ),
      );
    }
  }, [categoriesWithFiles, initialized]);

  if (isLoading) {
    return (
      <div className="flex h-full flex-col p-4">
        <div className="border-gray-200 border-b bg-white pb-4">
          <div className="rounded-lg border border-gray-200 border-dashed bg-white p-4">
            <div className="flex gap-4">
              <SkeletonPlaceholder
                height="h-12"
                rounded="rounded-lg"
                width="w-12"
                shrink0
              />
              <div className="flex-1 space-y-2">
                <SkeletonPlaceholder height="h-4" width="w-48" />
                <SkeletonPlaceholder height="h-3" width="w-64" />
                <div className="mt-3">
                  <SkeletonPlaceholder height="h-8" width="w-24" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto pt-4">
          <div className="divide-y divide-gray-200 rounded-lg border border-gray-200">
            {[...Array(3)].map((_, i) => {
              return (
                <div key={i} className="p-4">
                  <div className="flex items-center gap-2">
                    <SkeletonPlaceholder height="h-4" width="w-24" />
                    <SkeletonPlaceholder
                      height="h-5"
                      rounded="rounded-full"
                      width="w-12"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <EmptyState
        description="Case files are not available for this case"
        headline="No case data available"
        icon={faFiles}
        variant="default"
      />
    );
  }

  return (
    <div className="flex h-full flex-col p-4">
      <div className="border-gray-200 border-b bg-white pb-4">
        <div className="rounded-lg border border-gray-200 border-dashed bg-white p-4">
          <div className="flex gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-indigo-100">
              <Icon color="text-indigo-600" icon={faUpload} />
            </div>
            <div className="flex-1">
              <h3 className="font-inter-semibold-13px text-gray-800">
                Click here to upload
              </h3>
              <p className="mt-1 font-inter-normal-12px text-gray-500">
                {canEditFiles
                  ? `Supported types: ${ACCEPTED_FORMATS.join(", ")}`
                  : "Only workspace admins or assigned users can upload files"}
              </p>
              <div className="mt-3 flex justify-start">
                {canEditFiles && !isUploading ? (
                  <OptionsDropdown
                    buttonDataLoc="upload-file-dropdown"
                    elements={dropdownElements}
                    placement="bottom start"
                  >
                    <div className="flex w-28 cursor-pointer justify-between rounded-md border border-gray-300 bg-white py-0.5 pr-1.5 pl-2.5 font-inter-medium-12px text-gray-800 shadow-sm hover:bg-gray-50">
                      Upload a file
                      <Icon
                        color="text-gray-500"
                        icon={faChevronDown}
                        size="xs"
                      />
                    </div>
                  </OptionsDropdown>
                ) : (
                  <div className="flex w-28 cursor-not-allowed justify-between rounded-md border border-gray-300 bg-gray-50 py-0.5 pr-1.5 pl-2.5 font-inter-medium-12px text-gray-500 shadow-sm">
                    {isUploading ? "Uploading..." : "Upload a file"}
                    <Icon
                      color="text-gray-400"
                      icon={faChevronDown}
                      size="xs"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pt-4">
        {fileCategories.length > 0 ? (
          <Root
            className={accordionRootClassName}
            type="multiple"
            value={openAccordionItems}
            onValueChange={setOpenAccordionItems}
          >
            {fileCategories.map((category) => (
              <FileCategory
                key={category.category_key}
                canDelete={canDeleteFiles}
                canEdit={canEditFiles}
                category={category}
                env={env}
                fileCategories={fileCategories}
                newlyUploadedFileIds={newlyUploadedFileIds}
                onDeleteFile={handleDeleteFile}
                onMoveFile={handleMoveFile}
              />
            ))}
          </Root>
        ) : (
          <EmptyState
            description="No file categories have been configured for this case template"
            headline="No file categories"
            icon={faFiles}
            variant="default"
          />
        )}
      </div>

      <div className="hidden">
        <Dropzone
          ref={dropzoneRef}
          accept={ACCEPTED_FILE_TYPES}
          disabled={!canEditFiles || isUploading}
          maxFiles={1}
          maxSize={MAX_FILE_SIZE_BYTES}
          noClick
          noDrag
          onDrop={handleFileSelect}
        >
          {({ getRootProps, getInputProps }) => (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
            </div>
          )}
        </Dropzone>
      </div>
    </div>
  );
};
